import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Space, Typography } from "antd";
import { FaPlane } from "react-icons/fa";
import { useAppContext } from "../../contexts/AppContext";
import { resolveAirlineCodeFromPackageLeg } from "../Common/UnifiedPackageFlightLeg/packageFlightLegAdapter";
import "./style.css";

const { Text } = Typography;

function normalizeAirlineIdProp(airlineId) {
  if (airlineId == null) return null;
  const s = String(airlineId).trim();
  if (!s || s.toUpperCase() === "N/A") return null;
  return s;
}

const AirlineDisplay = ({ flightNumber, airlineId, leg, isCompact = false }) => {
  const { airlines } = useAppContext();

  const airline = useMemo(() => {
    const list = Array.isArray(airlines) ? airlines : [];
    if (!list.length) return undefined;

    const tryCode = (code) => {
      if (!code || String(code).trim() === "") return undefined;
      const u = String(code).trim().toUpperCase();
      return list.find(
        (a) => a?.code && String(a.code).trim().toUpperCase() === u
      );
    };

    const idNorm = normalizeAirlineIdProp(airlineId);

    if (leg) {
      const codeFromLeg = resolveAirlineCodeFromPackageLeg(leg, list);
      const fromLeg = tryCode(codeFromLeg);
      if (fromLeg) return fromLeg;
    }

    if (idNorm) {
      return (
        tryCode(idNorm) ||
        list.find(
          (a) =>
            a?.id != null &&
            String(a.id).toLowerCase() === idNorm.toLowerCase()
        )
      );
    }

    return undefined;
  }, [airlines, leg, airlineId]);

  if (isCompact) {
    return (
      <motion.div
        className="airline-compact"
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: 10,
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        whileHover={{ scale: 1.02 }}
      >
        {airline ? (
          <img
            src={airline.logo}
            alt={airline.name}
            className="airline-logo-compact"
            loading="eager"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling?.style?.display === 'none' && (e.target.nextSibling.style.display = 'flex');
            }}
          />
        ) : (
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
            className="airline-logo-fallback"
          >
            <FaPlane className="fallback-icon" />
          </motion.div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      className="airline-display"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
    >
      <Space
        direction="vertical"
        size="small"
        align="center"
        className="airline-content"
      >
        {airline ? (
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
            className="airline-logo-container"
          >
            <img
              src={airline.logo}
              alt={airline.name}
              className="airline-logo-image"
              loading="eager"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement?.nextSibling?.style?.display === 'none' && (e.target.parentElement.nextSibling.style.display = 'flex');
              }}
            />
          </motion.div>
        ) : (
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
            className="airline-logo-fallback"
          >
            <FaPlane className="fallback-icon" />
          </motion.div>
        )}

        <Space
          direction="vertical"
          size={2}
          align="center"
          className="airline-info"
        >
          {airline && (
            <Text
              className="airline-name-text"
              ellipsis={{ tooltip: airline.name }}
            >
              {airline.name}
            </Text>
          )}
          <Text className="flight-number" strong>
            {flightNumber}-{airlineId}
          </Text>
        </Space>
      </Space>
    </motion.div>
  );
};

export default AirlineDisplay;
